import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, wanneer, geleden } from '../i18n.js';
import { St, NessusErnst } from './Status.jsx';
import VerwachtePoorten from './VerwachtePoorten.jsx';

/**
 * De koppeling met Nessus (docs/besluiten/nessus-koppeling.md), zoals de API-gegevens van een FMC: eerst het
 * certificaat ophalen en bevestigen, dan de API-sleutels; de server test ze voordat hij ze (versleuteld) bewaart.
 * Daaronder: nu inlezen, wat er de laatste keren werd ingelezen, verwachte poorten en adressen die bij geen klant horen.
 */
export default function NessusKoppeling() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['nessus-koppeling'], queryFn: api.nessusKoppeling,
    refetchInterval: (s) => (s.state.data?.data.running ? 3000 : 30_000) });
  const [wijzig, zetWijzig] = useState(false);
  const [melding, zetMelding] = useState(null);
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const k = q.data.data;
  const ververs = () => { client.invalidateQueries({ queryKey: ['nessus-koppeling'] }); client.invalidateQueries({ queryKey: ['scans'] }); };
  const nu = async () => {
    zetMelding(null);
    try { await api.nessusNu(); ververs(); } catch (e) { zetMelding({ goed: false, tekst: t(e.message) }); }
  };
  const weg = async () => {
    zetMelding(null);
    try { await api.verwijderNessusKoppeling(); ververs(); } catch (e) { zetMelding({ goed: false, tekst: t(e.message) }); }
  };

  return (
    <div className="grid-2">
      <div className="stack">
        <section className="card">
          <header><h2>{t('Nessus')}</h2>{k.linked && <span className="meta">{k.running ? <St soort="warning">{t('Bezig met inlezen…')}</St>
            : k.syncError ? <St soort="critical">{t('Fout bij inlezen')}</St> : <St soort="good">{t('Gekoppeld')}</St>}</span>}</header>
          {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
          {k.linked && !wijzig ? (
            <>
              <dl className="facts">
                <dt>{t('Adres')}</dt><dd className="mono">{k.url}</dd>
                <dt>{t('Certificaat')}</dt><dd className="mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{k.fingerprint}</dd>
                <dt>{t('Ingesteld')}</dt><dd>{wanneer(k.setAt)}{k.setBy ? ` · ${k.setBy}` : ''}</dd>
                <dt>{t('Laatst ingelezen')}</dt><dd>{k.syncedAt ? geleden(k.syncedAt) : t('nog niet')}</dd>
              </dl>
              {k.syncError && <div className="callout warn" style={{ marginTop: 10 }}>{t(k.syncError)}</div>}
              <div className="actions">
                <button type="button" className="btn primary" disabled={k.running} onClick={nu}>{k.running ? t('Bezig…') : t('Nu inlezen')}</button>
                <button type="button" className="btn" onClick={() => zetWijzig(true)}>{t('Sleutels of adres wijzigen')}</button>
                <button type="button" className="btn danger" onClick={weg}>{t('Koppeling verwijderen')}</button>
              </div>
              <p className="noot">{t('ADTS ZERO leest elk kwartier de voltooide scans in. Alleen lezen: ADTS ZERO start of wijzigt geen scans.')}</p>
            </>
          ) : <Instellen standaardUrl={k.url ?? ''} onKlaar={(uit) => { zetWijzig(false); zetMelding({ goed: true, tekst: t('Gekoppeld met {type} {versie}.', { type: uit.type ?? 'Nessus', versie: uit.version ?? '' }) }); ververs(); }}
            onAnnuleer={k.linked ? () => zetWijzig(false) : null} />}
        </section>

        {k.imports.length > 0 && (
          <section className="card">
            <header><h2>{t('Laatst ingelezen scans')}</h2></header>
            <table>
              <thead><tr><th>{t('Scan')}</th><th>{t('Klaar in Nessus')}</th><th>{t('Adressen')}</th><th>{t('Bevindingen')}</th></tr></thead>
              <tbody>{k.imports.map((r) => (
                <tr key={`${r.scanId}-${r.historyId}`}>
                  <td>{r.name}<br /><span className="muted mono" style={{ fontSize: 12 }}>{t('scan {id}, run {run}', { id: r.scanId, run: r.historyId })}</span></td>
                  <td className="mono">{r.finishedAt ? wanneer(r.finishedAt) : '—'}</td><td className="mono">{getal(r.hosts)}</td><td className="mono">{getal(r.findings)}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
        )}
      </div>

      <div className="stack">
        <VerwachtePoorten />
        <section className="card">
          <header><h2>{t('Adressen zonder klant')}</h2><span className="meta">{getal(k.unassigned.length)}</span></header>
          {k.unassigned.length ? (
            <>
              <table>
                <thead><tr><th>{t('Adres')}</th><th>{t('Ernst')}</th><th>{t('Scan')}</th></tr></thead>
                <tbody>{k.unassigned.map((h) => (
                  <tr key={h.ip}><td className="mono">{h.ip}{h.hostname && <><br /><span className="muted">{h.hostname}</span></>}</td>
                    <td><NessusErnst e={h.maxSeverity} /></td><td className="muted">{h.scan}</td></tr>
                ))}</tbody>
              </table>
              <p className="noot">{t('Deze adressen vallen in geen IP-reeks van een klant, en zijn daarom bij geen klant te zien. Voeg de reeks toe onder Beheer › Klanten; wat al gescand is, gaat dan mee.')}</p>
            </>
          ) : <p className="empty">{t('Elk gescand adres hoort bij een klant.')}</p>}
        </section>
      </div>
    </div>
  );
}

function Instellen({ standaardUrl, onKlaar, onAnnuleer }) {
  const [v, zetV] = useState({ url: standaardUrl, accessKey: '', secretKey: '' });
  const [cert, zetCert] = useState(null);
  const [bevestigd, zetBevestigd] = useState(false);
  const [fout, zetFout] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const zet = (veld) => (e) => { const w = e.target.value; zetV((x) => ({ ...x, [veld]: w })); if (veld === 'url') { zetCert(null); zetBevestigd(false); } };
  const doe = async (fn) => {
    zetFout(null); zetBezig(true);
    try { await fn(); } catch (e) { zetFout(t(e.details ? Object.values(e.details)[0] : e.message)); } finally { zetBezig(false); }
  };
  const url = v.url.trim().replace(/\/$/, '');

  return (
    <form className="form" onSubmit={(e) => { e.preventDefault(); doe(async () => onKlaar((await api.zetNessusKoppeling({ ...v, url, fingerprint: cert.fingerprint })).data)); }}>
      {fout && <div className="melder rood">{fout}</div>}
      <label className="field"><span>{t('Adres van Nessus')}</span>
        <input className="input mono" value={v.url} onChange={zet('url')} placeholder="https://nessus.adts.local:8834" /></label>
      {!cert ? (
        <div className="actions" style={{ paddingTop: 0 }}><button type="button" className="btn" disabled={bezig || !/^https:\/\/.+/.test(url)}
          onClick={() => doe(async () => zetCert((await api.nessusCertificaat(url)).data))}>{bezig ? t('Bezig…') : t('Certificaat ophalen')}</button></div>
      ) : (
        <div className="callout" style={{ display: 'grid', gap: 6 }}>
          <span>{t('Certificaat van {naam}', { naam: cert.subject ?? url })}{cert.trusted ? '' : ` · ${t('zelfgetekend of niet vertrouwd')}`}{cert.expired ? ` · ${t('verlopen')}` : ''}</span>
          <span className="mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{cert.fingerprint}</span>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={bevestigd} onChange={(e) => zetBevestigd(e.target.checked)} />
            {t('Dit is het certificaat van onze Nessus (vergelijk de vingerafdruk in Nessus of op de server)')}</label>
        </div>
      )}
      {bevestigd && (
        <>
          <label className="field"><span>{t('Access key')}</span>
            <input className="input mono" value={v.accessKey} onChange={zet('accessKey')} autoComplete="off" spellCheck={false} /></label>
          <label className="field"><span>{t('Secret key')}</span>
            <input className="input mono" type="password" value={v.secretKey} onChange={zet('secretKey')} autoComplete="new-password" spellCheck={false} />
            <small>{t('In Nessus: My Account › API Keys › Generate, met een eigen gebruiker die alleen mag lezen. De secret key staat er maar één keer.')}</small></label>
        </>
      )}
      <div className="actions">
        <button className="btn primary" disabled={bezig || !bevestigd || !v.accessKey.trim() || !v.secretKey.trim()}>{bezig ? t('Bezig…') : t('Testen en opslaan')}</button>
        {onAnnuleer && <button type="button" className="btn" onClick={onAnnuleer}>{t('Annuleren')}</button>}
      </div>
      <p className="noot">{t('ADTS ZERO stuurt de sleutels pas als het certificaat klopt, test ze, en bewaart ze versleuteld. Ze komen niet terug in het scherm.')}</p>
    </form>
  );
}
